import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { AlertCircle, Clock } from "lucide-react";
import { fmtMoney } from "@/lib/money";

const BUCKETS = ["no_vencido", "d0_30", "d31_60", "d61_90", "d90_plus", "sin_vencimiento"];

export const AgingTab = ({ isGlobal, oficinas = [] }) => {
  const { api } = useAuth();
  const { t } = useI18n();
  const [tipo, setTipo] = useState("clientes");
  const [oficina, setOficina] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const params = { tipo };
    if (oficina) params.oficina = oficina;
    const { data: d } = await api.get("/accounting/aging", { params });
    setData(d);
  }, [api, tipo, oficina]);
  useEffect(() => { load().catch(() => setData(null)); }, [load]);

  const cur = data?.moneda || "EUR";
  const money = (v) => fmtMoney(v, cur);
  const inp = "px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";

  return (
    <div className="space-y-4" data-testid="acc-aging">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5">
          {["clientes", ...(isGlobal ? ["proveedores"] : [])].map(k => (
            <button
              key={k}
              data-testid={`aging-type-${k}`}
              onClick={() => setTipo(k)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold ${tipo === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
            >
              {t(k === "clientes" ? "receivables" : "payables")}
            </button>
          ))}
        </div>
        {isGlobal && (
          <select data-testid="aging-office" value={oficina} onChange={(e) => setOficina(e.target.value)} className={inp}>
            <option value="">{t("all_offices")}</option>
            {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="aging-total-pending">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-700"><Clock className="w-3.5 h-3.5" /> {t("outstanding_total")}</div>
              <div className="mt-1.5 text-2xl font-semibold tabular-nums text-amber-900">{money(data.total_pendiente)}</div>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="aging-total-overdue">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-red-700"><AlertCircle className="w-3.5 h-3.5" /> {t("overdue_total")}</div>
              <div className="mt-1.5 text-2xl font-semibold tabular-nums text-red-900">{money(data.total_vencido)}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {BUCKETS.map(b => (
              <div key={b} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`aging-bucket-${b}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t(`bucket_${b}`)}</div>
                <div className="mt-1 text-base font-semibold tabular-nums">{money(data.buckets[b] || 0)}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="p-3 text-start">{t("invoice_number")}</th>
                  <th className="p-3 text-start">{tipo === "clientes" ? t("client") : t("supplier")}</th>
                  <th className="p-3 text-start">{t("office")}</th>
                  <th className="p-3 text-start">{t("due_date")}</th>
                  <th className="p-3 text-end">{t("days_overdue")}</th>
                  <th className="p-3 text-end">{t("total")}</th>
                  <th className="p-3 text-end">{t("balance")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("nothing_overdue")}</td></tr>}
                {data.rows.map(r => (
                  <tr key={r.id} data-testid={`aging-row-${r.id}`} className={r.dias_vencido > 0 ? "bg-red-50/40" : ""}>
                    <td className="p-3 font-mono font-semibold">{r.numero}</td>
                    <td className="p-3">{r.entidad || "—"}</td>
                    <td className="p-3 text-slate-600">{r.oficina || "—"}</td>
                    <td className="p-3 tabular-nums text-slate-600">{r.vencimiento || t("bucket_sin_vencimiento")}</td>
                    <td className="p-3 text-end tabular-nums">
                      {r.dias_vencido > 0
                        ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{r.dias_vencido} d</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-3 text-end tabular-nums">{money(r.total)}</td>
                    <td className="p-3 text-end tabular-nums font-semibold">{money(r.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
